import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LockKeyhole, Megaphone, ShieldCheck } from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import type { HygieneRecallConfiguration } from './campaign-settings-contracts'
import type { CampaignSettingsRepository } from './campaign-settings-repository'
import styles from './SettingsPage.module.css'

const DEFAULT_CONFIGURATION: HygieneRecallConfiguration = {
  enabled: false,
  recallIntervalMonths: 6,
  qualifyingAppointmentTypeIds: [],
}

const CAMPAIGNS_AVAILABLE = false

function CampaignsComingSoon() {
  return (
    <section className={styles.campaignSettings}>
      <header className={styles.sectionHero}>
        <span className={styles.eyebrow}>Campaigns</span>
        <h2>Patient outreach</h2>
        <p>
          Campaign configuration is unavailable while the product is in
          development.
        </p>
      </header>
      <div
        className={`${styles.campaignAvailability} ${styles.campaignAvailabilityDisabled}`}
      >
        <div
          className={styles.campaignComingSoon}
          role="status"
          aria-labelledby="campaign-coming-soon-title"
          aria-describedby="campaign-coming-soon-description"
        >
          <div className={styles.campaignComingSoonCard}>
            <span className={styles.campaignComingSoonIcon}>
              <LockKeyhole size={22} aria-hidden="true" />
            </span>
            <span className={styles.eyebrow}>Campaigns</span>
            <h3 id="campaign-coming-soon-title">Coming Soon</h3>
            <p id="campaign-coming-soon-description">
              Campaigns are currently in development and will be available in a
              future update.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

export function CampaignSettings({
  tenantId,
  tenantLabel,
  repository,
}: {
  tenantId: string
  tenantLabel: string
  repository: CampaignSettingsRepository
}) {
  const queryClient = useQueryClient()
  const queryKey = ['settings', tenantId, 'campaigns']
  const settingsQuery = useQuery({
    queryKey,
    queryFn: () => repository.getSettings(tenantId),
    enabled: CAMPAIGNS_AVAILABLE,
  })
  const [draft, setDraft] = useState(DEFAULT_CONFIGURATION)

  useEffect(() => {
    const configuration = settingsQuery.data?.configuration
    if (configuration) setDraft(configuration.hygieneRecall)
  }, [settingsQuery.data])

  const providerAppointmentTypes =
    settingsQuery.data?.configuration?.appointmentTypes
  const appointmentTypes = useMemo(
    () => providerAppointmentTypes ?? [],
    [providerAppointmentTypes]
  )
  const allTypeIds = useMemo(
    () => appointmentTypes.map((item) => item.id).sort((a, b) => a - b),
    [appointmentTypes]
  )
  const baseline = settingsQuery.data?.configuration?.hygieneRecall
  const dirty = baseline
    ? JSON.stringify(draft) !== JSON.stringify(baseline)
    : false
  const valid =
    draft.recallIntervalMonths >= 3 &&
    draft.recallIntervalMonths <= 24 &&
    (!draft.enabled || draft.qualifyingAppointmentTypeIds.length > 0)

  const save = useMutation({
    mutationFn: () => {
      if (!CAMPAIGNS_AVAILABLE) {
        throw new Error('Campaigns are not currently available')
      }
      const current = settingsQuery.data?.configuration
      if (!current) throw new Error('Campaign settings unavailable')
      return repository.updateSettings(tenantId, {
        expectedConfigRevision: current.configRevision,
        expectedConfigFingerprint: current.configFingerprint,
        hygieneRecall: draft,
      })
    },
    onSuccess: (data) => queryClient.setQueryData(queryKey, data),
    onError: () => queryClient.invalidateQueries({ queryKey }),
  })

  if (!CAMPAIGNS_AVAILABLE) return <CampaignsComingSoon />
  if (settingsQuery.isPending) return <Spinner label="Loading campaigns" />
  if (settingsQuery.isError) {
    return (
      <StatePanel
        kind="error"
        title="Campaign settings unavailable"
        description="Campaign configuration could not be loaded safely."
      />
    )
  }
  if (
    settingsQuery.data.status !== 'AVAILABLE' ||
    !settingsQuery.data.configuration
  ) {
    return (
      <StatePanel
        kind="empty"
        title="Campaign provider data is not ready"
        description="A current NexHealth resource snapshot is required before campaign appointment types can be configured."
      />
    )
  }

  const configuration = settingsQuery.data.configuration
  const entitled = settingsQuery.data.platformEntitled

  const setSelected = (identifiers: number[]) =>
    setDraft({
      ...draft,
      qualifyingAppointmentTypeIds: [...new Set(identifiers)].sort(
        (a, b) => a - b
      ),
    })

  return (
    <section className={styles.campaignSettings}>
      <header className={styles.sectionHero}>
        <span className={styles.eyebrow}>Campaigns</span>
        <h2>Patient outreach</h2>
        <p>
          Configure reviewed outreach for <strong>{tenantLabel}</strong> using
          exact NexHealth appointment history and provider Appointment Types.
        </p>
      </header>

      <div className={styles.campaignAvailability}>
        <div
          className={styles.campaignLockedContent}
          data-testid="campaign-locked-content"
          aria-hidden="true"
          {...({ inert: '' } as { inert?: string })}
          onClickCapture={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onKeyDownCapture={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onChangeCapture={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onInputCapture={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onSubmitCapture={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          <Card className={styles.campaignCard}>
            <div className={styles.campaignHeader}>
              <span>
                <Megaphone size={19} aria-hidden="true" />
                <span>
                  <strong>Hygiene Recall</strong>
                  <small>
                    Reach eligible existing PMS patients when another cleaning
                    may be due and no qualifying future visit is scheduled.
                  </small>
                </span>
              </span>
              <label className={styles.campaignToggle}>
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  disabled={!entitled}
                  onChange={(event) =>
                    setDraft({ ...draft, enabled: event.currentTarget.checked })
                  }
                />
                {draft.enabled ? 'On' : 'Off'}
              </label>
            </div>

            {!entitled ? (
              <div className={styles.campaignNotice} role="status">
                <ShieldCheck size={17} aria-hidden="true" />
                <span>
                  <strong>
                    Campaign capability is not active for this clinic.
                  </strong>
                  Configuration can be prepared, but Hygiene Recall cannot be
                  turned on until the existing communications campaign
                  entitlement is reviewed and activated.
                </span>
              </div>
            ) : null}

            <div className={styles.campaignForm}>
              <label>
                Recall after
                <span className={styles.inlineNumberField}>
                  <input
                    aria-label="Recall interval in months"
                    type="number"
                    min={3}
                    max={24}
                    value={draft.recallIntervalMonths}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        recallIntervalMonths: Number(event.currentTarget.value),
                      })
                    }
                  />
                  months
                </span>
                <small>Allowed range: 3–24 months. Default: 6 months.</small>
              </label>

              <fieldset className={styles.appointmentTypeSelector}>
                <legend>Qualifying NexHealth Appointment Types</legend>
                <p>
                  Select only the provider types the clinic has reviewed as
                  hygiene visits. Names are shown for operators; exact provider
                  IDs are preserved.
                </p>
                <div className={styles.selectorActions}>
                  <button type="button" onClick={() => setSelected(allTypeIds)}>
                    Select all
                  </button>
                  <button type="button" onClick={() => setSelected([])}>
                    Clear all
                  </button>
                </div>
                <div className={styles.appointmentTypeList}>
                  {appointmentTypes.map((item) => {
                    const checked = draft.qualifyingAppointmentTypeIds.includes(
                      item.id
                    )
                    return (
                      <label key={item.id}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) =>
                            setSelected(
                              event.currentTarget.checked
                                ? [
                                    ...draft.qualifyingAppointmentTypeIds,
                                    item.id,
                                  ]
                                : draft.qualifyingAppointmentTypeIds.filter(
                                    (identifier) => identifier !== item.id
                                  )
                            )
                          }
                        />
                        <span>
                          <strong>{item.name}</strong>
                          <small>
                            NexHealth ID {item.id} · {item.minutes} min
                          </small>
                        </span>
                      </label>
                    )
                  })}
                </div>
              </fieldset>

              {configuration.configurationState === 'REVIEW_REQUIRED' ? (
                <p className={styles.validationError} role="alert">
                  One or more selected Appointment Types no longer exist in the
                  current NexHealth resource authority. Review and save before
                  this campaign can be active.
                </p>
              ) : null}
              {draft.enabled &&
              draft.qualifyingAppointmentTypeIds.length === 0 ? (
                <p className={styles.validationError} role="alert">
                  Select at least one qualifying Appointment Type before
                  enabling Hygiene Recall.
                </p>
              ) : null}
              <p className={styles.campaignExecutionState}>
                Execution remains unavailable until canonical PMS history
                coverage can prove each patient’s complete recall cycle. Saving
                these settings does not send messages.
              </p>
            </div>

            <div className={styles.settingsActions}>
              {save.isSuccess ? <span>Settings saved</span> : null}
              {save.isError ? (
                <span className={styles.validationError} role="alert">
                  Campaign settings were not saved. Reload and try again.
                </span>
              ) : null}
              <Button
                variant="secondary"
                disabled={!dirty || save.isPending}
                onClick={() => baseline && setDraft(baseline)}
              >
                Cancel
              </Button>
              <Button
                disabled={!dirty || !valid || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </Card>
        </div>

        <div
          className={styles.campaignComingSoon}
          role="status"
          aria-labelledby="campaign-coming-soon-title"
          aria-describedby="campaign-coming-soon-description"
        >
          <div className={styles.campaignComingSoonCard}>
            <span className={styles.campaignComingSoonIcon}>
              <LockKeyhole size={22} aria-hidden="true" />
            </span>
            <span className={styles.eyebrow}>Campaigns</span>
            <h3 id="campaign-coming-soon-title">Coming Soon</h3>
            <p id="campaign-coming-soon-description">
              Campaigns are currently in development and will be available in a
              future update.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
